import { VitalType } from "@1games-ru/region-shared/dist/enums/effect/effect";
import { VitalDto } from "@1games-ru/region-shared/dist/types/player/player";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { percentByRange } from "@/core/helpers/percentOfRange";
import { CircleIndicator } from "@/shared/ui";
import InventoryCharacterEffectType from "@/domains/inventory/components/character/InventoryCharacterEffectType";

type Props = {
  type: VitalType;
  data: VitalDto[keyof VitalDto];
};

const accentColor: Record<VitalType, string> = {
  [VitalType.Satiety]: "#FFB45C",
  [VitalType.Health]: "#84FF97",
};

export default function InventoryCharacterVital({ type, data }: Props) {
  const { t } = useTranslation("inventory");
  const { min, max, value } = data;

  const vitalPercent = percentByRange(min, max, value);
  const color = accentColor[type];

  return (
    <div
      className={clsx("group z-2 flex items-center h-[2.375rem]", "relative rounded-full group", {
        "bg-[#84FF97]/20 text-[#84FF97]": type === VitalType.Health,
        "bg-[#FFB45C]/20 text-[#FFB45C]": type === VitalType.Satiety,
      })}
    >
      <div className="relative h-[2.375rem] aspect-square shrink-0 rounded-full outline outline-dark-900">
        <CircleIndicator
          fillPercent={vitalPercent}
          className="absolute h-full w-full"
          accentColor={color}
        />
        <div className="relative size-full grid place-content-center">
          <InventoryCharacterEffectType
            type={type}
            percent={vitalPercent}
            onlyIcon
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      </div>
      {!!data.effects.length && (
        <div className="px-2.5 flex items-center gap-2">
          {data.effects.map((effect) => (
            <InventoryCharacterEffectType
              key={effect.type}
              type={effect.type}
              startedAt={effect.startedAt}
              finishedAt={effect.finishedAt}
              onlyIcon
            />
          ))}
        </div>
      )}

      {!!data.effects.length && (
        <div className="group-hover:block hidden absolute top-full right-0 min-w-full w-fit">
          <div
            className={clsx("relative mt-1 flex flex-col rounded-[0.625rem]", {
              "bg-[#84FF97]/20": type === VitalType.Health,
              "bg-[#FFB45C]/20": type === VitalType.Satiety,
            })}
          >
            <span
              className={clsx(
                "absolute top-0 inset-x-0 h-[calc(100%-0.250rem)] border-transparent border-t-[0.125rem] rounded-lg mask-padding",
              )}
              style={{
                background: `linear-gradient(to right, transparent, ${color}40, ${color}, ${color}40, transparent)`,
              }}
            />
            <InventoryCharacterEffectType
              type={type}
              label={t(`inventory.${type.toLocaleLowerCase()}`)}
              percent={vitalPercent}
              className="rounded-[inherit] bg-[inherit]"
            />

            {data.effects.map((effect) => (
              <InventoryCharacterEffectType
                key={effect.type}
                type={effect.type}
                label={t(`inventory.${effect.type.toLocaleLowerCase()}`)}
                startedAt={effect.startedAt}
                finishedAt={effect.finishedAt}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
